import { _t } from "@cruisen/i18n"
import { CabinGradeType, TripSearchResultTag } from "@cruisen/contract"
import { CruiseSearchResult } from "../types"

export type FilterRange = { low: number | null; high: number | null }

export interface SelectedFilters {
  priceFilters: FilterRange
  shipYearFilters: FilterRange
  selectedShips: string[]
  selectedCruiseLines: string[]
  selectedDurations: string[]
  selectedOriginPorts: string[]
  selectedDestinationPorts: string[]
  selectedAgentLikedCruise?: boolean
  selectedTags?: TripSearchResultTag[]
  selectedStartDate: string | null
  selectedEndDate: string | null
  selectedCabinGradeTypes: CabinGradeType[]
}

export type FilterData = {
  price: {
    min: number
    max: number
  }
  shipLaunchDates: {
    min: number
    max: number
  }
  durations: { id: string; label: string }[]
  shipNames: string[]
  cruiseLineNames: string[]
}

const durationOrder = [
  "TwoToFiveDays",
  "SixToNineDays",
  "TenToFourteenDays",
  "FifteenToSeventeenDays",
  "EighteenPlusDays",
]

const durationFilters: Record<string, { from: number; to?: number }> = {
  TwoToFiveDays: { from: 2, to: 5 },
  SixToNineDays: { from: 6, to: 9 },
  TenToFourteenDays: { from: 10, to: 14 },
  FifteenToSeventeenDays: { from: 15, to: 17 },
  EighteenPlusDays: { from: 18 },
}

/**
 * Calculate the number of days between two dates
 *
 * @param {Date} startDate
 * @param {Date} endDate
 * @return {number}
 */
export const daysBetweenDates = (startDate: Date, endDate: Date) =>
  (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)

type SliderLimits = { min: number; max: number }

/**
 * Adjust the slider limits to the nearest multiple of the adjustNumber
 *
 * @param {SliderLimits} originalLimits
 * @param {number} adjustNumber
 * @return {SliderLimits}
 */
export function adjustSliderLimits(originalLimits: SliderLimits, adjustNumber = 500) {
  const min = Math.max(Math.floor(originalLimits.min / adjustNumber) * adjustNumber, 0)
  const max = Math.ceil(originalLimits.max / adjustNumber) * adjustNumber

  return { min, max }
}

export const getCruiseFilterData = (searchResults: CruiseSearchResult[]): FilterData | null => {
  if (searchResults.length === 0) return null

  let minPrice = Infinity
  let maxPrice = -Infinity

  let oldestShipLaunchYear = Infinity
  let latestShipLaunchYear = -Infinity

  const durationLabels = {
    TwoToFiveDays: _t("cruiseFilteringModal.days", { amount: "2-5" }),
    SixToNineDays: _t("cruiseFilteringModal.days", { amount: "6-9" }),
    TenToFourteenDays: _t("cruiseFilteringModal.days", { amount: "10-14" }),
    FifteenToSeventeenDays: _t("cruiseFilteringModal.days", { amount: "15-17" }),
    EighteenPlusDays: _t("cruiseFilteringModal.days", { amount: "18+" }),
  }

  const shipNamesOrderMap = new Map<string, string[]>()
  const cruiseLineNames = new Set<string>()
  const durations = new Set<string>()

  for (const { pricePP, startDate, endDate, shipName, lineName, shipLaunchDate } of searchResults) {
    if (pricePP.price !== -1) minPrice = Math.min(minPrice, pricePP.price)
    maxPrice = Math.max(maxPrice, pricePP.price)

    const currentDays = daysBetweenDates(startDate, endDate)

    for (const [key, { from, to }] of Object.entries(durationFilters)) {
      if (currentDays >= from && (to === undefined || currentDays <= to)) {
        durations.add(key)
        break
      }
    }

    if (!isNaN(shipLaunchDate.getTime())) {
      const shipLaunchYear = shipLaunchDate.getUTCFullYear()

      oldestShipLaunchYear = Math.min(oldestShipLaunchYear, shipLaunchYear)
      latestShipLaunchYear = Math.max(latestShipLaunchYear, shipLaunchYear)
    }

    const shipOrder = shipNamesOrderMap.get(lineName)
    shipNamesOrderMap.set(lineName, shipOrder ? [...shipOrder, shipName] : [shipName])

    cruiseLineNames.add(lineName)
  }

  const shipNames: string[] = Array.from(shipNamesOrderMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .reduce<string[]>((acc, [_, ships]) => {
      return [...acc, ...Array.from(new Set(ships)).sort((a, b) => a.localeCompare(b))]
    }, [])

  return {
    price: adjustSliderLimits(
      {
        min: Math.floor(minPrice === Infinity ? 0 : minPrice),
        max: Math.ceil(maxPrice === -Infinity ? 0 : maxPrice),
      },
      100,
    ),
    durations: Array.from(durations)
      .map((id) => ({ id, label: durationLabels[id] }))
      .sort((a, b) => durationOrder.indexOf(a.id) - durationOrder.indexOf(b.id)),
    shipLaunchDates: {
      min: oldestShipLaunchYear === Infinity ? 0 : oldestShipLaunchYear,
      max: latestShipLaunchYear === -Infinity ? 0 : latestShipLaunchYear,
    },
    shipNames,
    cruiseLineNames: Array.from(cruiseLineNames).sort((a, b) => a.localeCompare(b)),
  }
}

export const filterCruises = (
  cruises: CruiseSearchResult[],
  selectedFilters: SelectedFilters | null,
  tripAgentIds?: string[],
) => {
  if (selectedFilters === null) {
    return cruises.filter((cruise) => {
      // cruise price is lower than 0 when there is no cabins available
      if ("price" in cruise && cruise.price <= 0) {
        return false
      }
      return true
    })
  }

  const {
    priceFilters: { low, high },
    shipYearFilters,
    selectedShips,
    selectedCruiseLines,
    selectedDurations,
    selectedOriginPorts,
    selectedDestinationPorts,
    selectedStartDate,
    selectedEndDate,
    selectedCabinGradeTypes = [],
  } = selectedFilters

  const selectedAgentLikedCruise = selectedFilters?.selectedTags?.includes("agentPick")
  const selectedTags = selectedFilters?.selectedTags?.filter((tag) => tag !== "agentPick")
  const hasSelectedShips = selectedShips.length > 0
  const hasSelectedCruiseLines = selectedCruiseLines.length > 0
  const hasSelectedOriginPorts = selectedOriginPorts.length > 0
  const hasSelectedDestinationPorts = selectedDestinationPorts.length > 0
  const hasSelectedCabinGradeTypes = selectedCabinGradeTypes.length > 0
  // Prevent dates from being converted to a string
  const formattedSelectedStartDate = selectedStartDate ? new Date(selectedStartDate) : null
  const formattedSelectedEndDate = selectedEndDate ? new Date(selectedEndDate) : null

  return cruises.filter((cruise) => {
    const { price, pricePP, shipName, lineName, startDate } = cruise

    // cruise price is lower than 0 when there is no cabins available
    if (price <= 0) {
      return false
    }

    if (selectedTags?.length && selectedTags.some((tag) => !cruise.tags?.includes(tag))) {
      return false
    }

    if (low && pricePP.price < low) {
      return false
    } else if (high && pricePP.price > high) {
      return false
    } else if (hasSelectedShips && !selectedShips.includes(shipName)) {
      return false
    } else if (hasSelectedCruiseLines && !selectedCruiseLines.includes(lineName)) {
      return false
    } else if (shipYearFilters.low && cruise.shipLaunchDate.getFullYear() < shipYearFilters.low) {
      return false
    } else if (shipYearFilters.high && cruise.shipLaunchDate.getFullYear() > shipYearFilters.high) {
      return false
    } else if (
      hasSelectedDestinationPorts &&
      !cruise.ports.some((port, index) => {
        if (index === 0 || index === cruise.ports.length - 1) return false
        return selectedDestinationPorts.includes(port)
      })
    ) {
      return false
    } else if (hasSelectedOriginPorts && !selectedOriginPorts.includes(cruise.ports[0])) {
      return false
    } else if (
      selectedAgentLikedCruise &&
      tripAgentIds &&
      !cruise.likes.some((like) => tripAgentIds.includes(like))
    ) {
      return false
    } else if (
      hasSelectedCabinGradeTypes &&
      !cruise.cabinGradeTypes?.some((grade) => selectedCabinGradeTypes.includes(grade))
    ) {
      return false
    }

    const durationRanges = selectedDurations.map((value) => durationFilters[value])
    const cruiseDuration = daysBetweenDates(cruise.startDate, cruise.endDate)

    if (
      durationRanges.length !== 0 &&
      !durationRanges.some((range) => {
        const fromValid = range.from ? cruiseDuration >= range.from : true
        const toValid = range.to ? cruiseDuration <= range.to : true
        return fromValid && toValid
      })
    ) {
      return false
    }

    const departureTime = startDate.getTime()
    // Filter by start date with timezone offset (calendar dates are in LOCAL)
    if (formattedSelectedStartDate && departureTime < formattedSelectedStartDate.getTime()) {
      return false
    } else if (formattedSelectedEndDate && departureTime > formattedSelectedEndDate.getTime()) {
      return false
    }

    return true
  })
}
